import {
  Box,
  Button,
  Dialog,
  Switch,
  Backdrop,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  DialogActions,
  DialogContent,
  svgIconClasses,
  CircularProgress,
  FormControlLabel,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { Role } from 'library/types';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import apiService from 'library/api-service';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';

export type CreateActivityTypeFormParams = {
  roleIds: number[];
  title: string;
  isEffortActivity: boolean;
  isPermissionActivity: boolean;
};

const defaultValues: CreateActivityTypeFormParams = {
  roleIds: [],
  title: '',
  isEffortActivity: false,
  isPermissionActivity: false,
};

type CreateActivityTypeDialogProps = {
  open: boolean;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const CreateActivityTypeDialog = ({ open, handleClose: closeDialog, refresh }: CreateActivityTypeDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);

  // form validations & form state
  const schema = yup.object().shape({
    title: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    roleIds: yup
      .array()
      .of(
        yup
          .number()
          .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      )
      .min(
        1,
        intl.formatMessage({
          id: 'validations.activityTypes.role.minOne',
          defaultMessage: 'En az 1 rol seçilmelidir.',
        })
      )
      .required(),
  });

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateActivityTypeFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: CreateActivityTypeFormParams) => {
    try {
      setLoading(true);

      const response = await apiService.createActivityType(data);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityTypes.createActivityTypeCompleted"
            defaultMessage="Aktivite Türü oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getRoles = async () => {
    try {
      setLoadingRoles(true);

      const response = await apiService.getRoles();

      if (response.isSuccess) {
        setRoles(response.data);
      }
    } catch {
    } finally {
      setLoadingRoles(false);
    }
  };

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
    setSelectedRoles([]);
  };

  // useEffect hooks
  useEffect(() => {
    if (open) {
      getRoles();
    }
  }, [open]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="activityTypes.createActivityType" defaultMessage="Aktivite Türü Oluştur" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="activityTypes.createActivityTypeText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir aktivite türü oluşturabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                size="small"
                multiple
                filterSelectedOptions
                disableCloseOnSelect
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                options={roles}
                loading={loadingRoles}
                value={selectedRoles}
                {...renderAutoCompleteLocaleText()}
                onChange={(_, newValue: Role[]) => {
                  setSelectedRoles(newValue);

                  setValue(
                    'roleIds',
                    newValue.map((v) => v.id)
                  );
                }}
                getOptionLabel={(option: Role) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        width: 'fit-content',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                    label={<FormattedMessage id="activityTypes.roles" defaultMessage="Roller" />}
                    error={!!errors.roleIds}
                    helperText={errors.roleIds?.message}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingRoles ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="title"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    label={<FormattedMessage id="activityTypes.title" defaultMessage="Adı" />}
                    fullWidth
                    size="small"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="isEffortActivity"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={
                      <FormattedMessage id="activityTypes.isEffortActivity" defaultMessage="Efor Aktivitesi mi?" />
                    }
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="isPermissionActivity"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={
                      <FormattedMessage id="activityTypes.isPermissionActivity" defaultMessage="İzin Aktivitesi mi?" />
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default CreateActivityTypeDialog;
